import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  Plus,
  RefreshCcw,
  Search,
  Server,
  Activity,
  Globe2,
  X,
  CheckCircle2,
  AlertTriangle,
  Pencil,
  Trash2,
  ArrowRight,
  ArrowLeft,
  KeyRound,
  Link2,
  ShieldCheck,
  Cpu,
  Lock,
  Unlock,
  Zap,
  Terminal,
  SlidersHorizontal,
  Save,
  BadgeDollarSign,
  Gauge,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import { Topbar } from "@/components/dashboard/Topbar";
import { GlassPanel } from "@/components/dashboard/GlassPanel";
import { tooltipStyle, axisTick, gridStroke } from "@/components/dashboard/chartStyle";
import { cn } from "@/lib/utils";
import { RouteBadge } from "@/components/genrl/RouteBadge";
import { MICRO, type BroadcastMode, type PricingMode } from "@/lib/genrl";
import { APIError, apiRequest } from "@/lib/api";
import {
  assignHardwareCostBands,
  hardwareCostBandLabel,
  type HardwareCostBand,
} from "@/lib/hardware-cost-band";
import { toast } from "sonner";

export const Route = createFileRoute("/providers")({
  head: () => ({ meta: [{ title: "Providers · GENRL" }] }),
  component: ProvidersPage,
});

/* ---------------- types & helpers ---------------- */

type Source = {
  sourceId?: string;
  key: string;
  provider: string;
  baseUrl: string;
  offersPath: string;
  balancePath: string;
  apiKeyHeader: string;
  provisionAPIKeyHeader: string;
  status: "active" | "paused";
  primary: boolean;
  hasApiKey: boolean;
  broadcastMode: BroadcastMode;
  pricingMode: PricingMode;
  adjustmentBps: number; // for premium / discount
  fixedUnitMicros: number; // seller-selected fixed listing price
  minUnitMicros: number; // guardrail floor
  maxUnitMicros: number; // guardrail ceiling
  thresholdMicros: number; // balance threshold
  stockLimit?: number; // broadcast stock limit
  allowedRegions: string[];
  allowedHardware: string[];
};

type MarketOffer = {
  flavor_name?: string;
  gpu?: string;
  gpu_count?: number;
  price_per_hour?: number;
  currency?: string;
  provider_name?: string;
  provider_slug?: string;
  source?: string;
  region_name?: string;
  stock_available?: number;
  route_type?: string;
  raw_unit_cost_micros?: number | string;
  provider_unit_price_micros?: number | string;
  genrl_fee_bps?: number;
  genrl_unit_fee_micros?: number | string;
  buyer_unit_price_micros?: number | string;
};

type MarketSourceHealth = {
  source?: string;
  status?: string;
  code?: string;
};

type MarketOffersPayload = {
  offers?: MarketOffer[];
  source_health?: MarketSourceHealth[];
};

type CapacityEntry = {
  signal?: {
    spoke_id?: string;
    avail_count?: number;
  };
};

type ProviderBalancesPayload = {
  source_health?: MarketSourceHealth[];
};

type ProviderSourcePublic = {
  id: string;
  source_slug: string;
  display_name: string;
  base_url: string;
  offers_path: string;
  balance_path?: string;
  api_key_header?: string;
  provision_api_key_header?: string;
  active: boolean;
  has_api_key?: boolean;
  broadcast_mode?: BroadcastMode;
  pricing_mode?: PricingMode;
  provider_adjustment_bps?: number;
  fixed_unit_price_micros?: number;
  min_unit_price_micros?: number;
  max_unit_price_micros?: number;
  reserve_balance_threshold_micros?: number;
  broadcast_stock_limit?: number;
  allowed_regions?: string[];
  allowed_hardware?: string[];
};

function moneyMicros(value: number | string | undefined | null): number {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? Math.max(0, Math.round(parsed)) : 0;
}

function creditsToMicros(value: string | number | undefined): number {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? Math.max(0, Math.round(parsed * MICRO)) : 0;
}

function parseTokenList(raw: string): string[] {
  const seen = new Set<string>();
  return raw
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .filter((part) => {
      const key = part.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function normalizeAPITokenList(value?: string[]): string[] {
  return Array.isArray(value) ? parseTokenList(value.join(",")) : [];
}

function sourceFromAPI(row: ProviderSourcePublic, i: number): Source {
  return {
    sourceId: String(row.id || "").trim(),
    key: String(row.source_slug || `source_${i}`),
    provider: String(row.display_name || row.source_slug || `Source ${i + 1}`),
    baseUrl: String(row.base_url || ""),
    offersPath: String(row.offers_path || "/"),
    balancePath: String(row.balance_path || "/billing/user-credit/credit"),
    apiKeyHeader: String(row.api_key_header || "Authorization"),
    provisionAPIKeyHeader: String(
      row.provision_api_key_header || row.api_key_header || "Authorization",
    ),
    status: row.active ? "active" : "paused",
    primary: ["hyperstack", "external"].includes(String(row.source_slug || "").toLowerCase()),
    hasApiKey: !!row.has_api_key,
    broadcastMode: row.broadcast_mode || "BOTH",
    pricingMode: row.pricing_mode || "PASSTHROUGH",
    adjustmentBps: Number.isFinite(Number(row.provider_adjustment_bps))
      ? Number(row.provider_adjustment_bps)
      : 0,
    fixedUnitMicros: Number(row.fixed_unit_price_micros || 0),
    minUnitMicros: Number(row.min_unit_price_micros || 0),
    maxUnitMicros: Number(row.max_unit_price_micros || 0),
    thresholdMicros: Number(row.reserve_balance_threshold_micros || 0),
    stockLimit: Number(row.broadcast_stock_limit || 0) || undefined,
    allowedRegions: normalizeAPITokenList(row.allowed_regions),
    allowedHardware: normalizeAPITokenList(row.allowed_hardware),
  };
}

function effectivePrice(
  s: Source,
  rawUnitMicros?: number,
): { raw: number; provider: number; fee: number; buyer: number } {
  const raw = moneyMicros(rawUnitMicros || s.fixedUnitMicros || s.minUnitMicros || MICRO);
  let provider = raw;
  if (s.pricingMode === "PREMIUM_OR_DISCOUNT") {
    provider = Math.round((raw * (10_000 + s.adjustmentBps)) / 10_000);
  } else if (s.pricingMode === "FIXED" && s.fixedUnitMicros > 0) {
    provider = s.fixedUnitMicros;
  } else if (s.pricingMode === "FLOOR_ADJUSTED") {
    provider = Math.round((raw * (10_000 + s.adjustmentBps)) / 10_000);
  }
  if (s.minUnitMicros > 0 && provider < s.minUnitMicros) provider = s.minUnitMicros;
  if (s.maxUnitMicros > 0 && provider > s.maxUnitMicros) provider = s.maxUnitMicros;
  if (s.broadcastMode === "INTERNAL_ONLY") {
    return { raw, provider, fee: 0, buyer: 0 };
  }
  const fee = Math.round(provider * 0.08); // 800 bps default
  return { raw, provider, fee, buyer: provider + fee };
}

function offerSourceKey(offer: MarketOffer): string {
  return String(offer.source || offer.provider_slug || offer.provider_name || "")
    .trim()
    .toLowerCase();
}

function offerHardwareLabel(offer: MarketOffer): string {
  const gpu = String(offer.gpu || "").trim();
  const flavor = String(offer.flavor_name || "").trim();
  if (gpu && flavor && !flavor.toLowerCase().includes(gpu.toLowerCase()))
    return `${gpu} · ${flavor}`;
  return gpu || flavor || "GPU";
}

function offerRawMicros(offer: MarketOffer): number {
  return moneyMicros(offer.raw_unit_cost_micros) || creditsToMicros(offer.price_per_hour || 0);
}

function offerProviderMicros(offer: MarketOffer): number {
  return moneyMicros(offer.provider_unit_price_micros) || offerRawMicros(offer);
}

function offerBuyerMicros(offer: MarketOffer): number {
  const explicit = moneyMicros(offer.buyer_unit_price_micros);
  if (explicit > 0) return explicit;
  return offerProviderMicros(offer) + moneyMicros(offer.genrl_unit_fee_micros);
}

function offersForSource(offers: MarketOffer[], source: Source): MarketOffer[] {
  const sourceKey = source.key.toLowerCase();
  const providerKey = source.provider.toLowerCase();
  return offers.filter((offer) => {
    const tokens = [
      offerSourceKey(offer),
      String(offer.provider_slug || "").toLowerCase(),
      String(offer.provider_name || "").toLowerCase(),
    ].filter(Boolean);
    return tokens.some(
      (token) => token === sourceKey || token === providerKey || token.includes(sourceKey),
    );
  });
}

type ProviderHardwareOption = {
  key: string;
  label: string;
  regions: string[];
  stock: number;
  costBand: HardwareCostBand;
};

function providerHardwareOptions(
  sourceOffers: readonly MarketOffer[],
  source: Source,
): ProviderHardwareOption[] {
  const allowedRegions = source.allowedRegions.map((region) => region.toLowerCase());
  const allowedHardware = source.allowedHardware.map((hardware) => hardware.toLowerCase());
  const groups = new Map<
    string,
    {
      key: string;
      label: string;
      regions: Set<string>;
      stock: number;
      buyerMicros?: number;
    }
  >();

  sourceOffers.forEach((offer) => {
    const stock = Math.max(0, Number(offer.stock_available || 0));
    if (stock <= 0) return;
    const region = String(offer.region_name || "").trim();
    if (allowedRegions.length > 0 && (!region || !allowedRegions.includes(region.toLowerCase()))) {
      return;
    }
    const label = offerHardwareLabel(offer);
    const normalizedLabel = label.toLowerCase();
    if (
      allowedHardware.length > 0 &&
      !allowedHardware.some(
        (allowed) => normalizedLabel.includes(allowed) || allowed.includes(normalizedLabel),
      )
    ) {
      return;
    }

    const key = normalizedLabel;
    const buyerMicros =
      offerBuyerMicros(offer) || offerProviderMicros(offer) || offerRawMicros(offer) || undefined;
    const current = groups.get(key);
    if (current) {
      current.stock += stock;
      if (region) current.regions.add(region);
      if (
        buyerMicros !== undefined &&
        (current.buyerMicros === undefined || buyerMicros < current.buyerMicros)
      ) {
        current.buyerMicros = buyerMicros;
      }
      return;
    }
    groups.set(key, {
      key,
      label,
      regions: new Set(region ? [region] : []),
      stock,
      buyerMicros,
    });
  });

  const displayed = [...groups.values()]
    .sort((left, right) => {
      const leftCost = left.buyerMicros ?? Number.POSITIVE_INFINITY;
      const rightCost = right.buyerMicros ?? Number.POSITIVE_INFINITY;
      return leftCost - rightCost || left.label.localeCompare(right.label);
    })
    .slice(0, 8);
  const costBands = assignHardwareCostBands(displayed, (option) => option.buyerMicros);

  return displayed.map((option, index) => ({
    key: option.key,
    label: option.label,
    regions: [...option.regions].sort((left, right) => left.localeCompare(right)),
    stock: option.stock,
    costBand: costBands[index] || "Mid cost",
  }));
}

function providerSourceBody(s: Source, apiKey: string): Record<string, unknown> {
  const body: Record<string, unknown> = {
    id: s.sourceId,
    source_slug: s.key,
    provider_slug: s.key,
    display_name: s.provider,
    base_url: s.baseUrl,
    offers_path: s.offersPath,
    balance_path: s.balancePath,
    api_key_header: s.apiKeyHeader,
    provision_base_url: s.baseUrl,
    provision_api_key_header: s.provisionAPIKeyHeader || s.apiKeyHeader,
    broadcast_mode: s.broadcastMode,
    pricing_mode: s.pricingMode,
    provider_adjustment_bps: s.adjustmentBps,
    fixed_unit_price_micros: s.fixedUnitMicros,
    min_unit_price_micros: s.minUnitMicros,
    max_unit_price_micros: s.maxUnitMicros,
    reserve_balance_threshold_micros: s.thresholdMicros,
    broadcast_stock_limit: s.stockLimit ?? 0,
    allowed_regions: s.allowedRegions,
    allowed_hardware: s.allowedHardware,
    active: s.status === "active",
  };
  const nextAPIKey = apiKey.trim();
  if (nextAPIKey) {
    body.api_key = nextAPIKey;
    body.provision_api_key = nextAPIKey;
  }
  return body;
}

const BROADCAST_LABEL: Record<BroadcastMode, string> = {
  INTERNAL_ONLY: "Internal only",
  BROADCAST_ONLY: "Broadcast",
  BOTH: "Both",
};
const PRICING_LABEL: Record<PricingMode, string> = {
  PASSTHROUGH: "Passthrough",
  PREMIUM_OR_DISCOUNT: "Premium / discount",
  FIXED: "Fixed",
  FLOOR_ADJUSTED: "Floor-adjusted",
};

const TONE: Record<string, string> = {
  violet: "text-[oklch(0.70_0.18_270)]",
  mint: "text-[oklch(0.80_0.18_145)]",
  cyan: "text-[oklch(0.78_0.16_200)]",
  amber: "text-[oklch(0.82_0.17_80)]",
};

function StatChip({
  label,
  value,
  hint,
  icon: Icon,
  tone,
}: {
  label: string;
  value: string | number;
  hint: string;
  icon: LucideIcon;
  tone: keyof typeof TONE;
}) {
  return (
    <div className="glass flex items-center gap-3 rounded-xl px-4 py-3">
      <div
        className={cn(
          "flex h-9 w-9 items-center justify-center rounded-lg border hairline bg-soft",
          TONE[tone],
        )}
      >
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </p>
        <div className="flex items-baseline gap-2">
          <span className="text-lg font-semibold tracking-tight text-foreground">{value}</span>
          <span className="truncate text-[11px] text-muted-foreground">{hint}</span>
        </div>
      </div>
    </div>
  );
}

function sourceStatus(
  s: Source,
  healthBySource: Record<string, string>,
): { label: string; cls: string; icon: LucideIcon } {
  if (s.status === "paused")
    return {
      label: "Paused",
      cls: "border-[var(--warning)]/30 bg-[var(--warning)]/10 text-[var(--warning)]",
      icon: AlertTriangle,
    };
  if (!s.hasApiKey)
    return {
      label: "Needs API key",
      cls: "border-destructive/30 bg-destructive/10 text-destructive",
      icon: KeyRound,
    };
  const health = String(healthBySource[s.key] || "").toLowerCase();
  if (health === "auth_error")
    return {
      label: "Auth error",
      cls: "border-destructive/30 bg-destructive/10 text-destructive",
      icon: AlertTriangle,
    };
  if (health === "fetch_error")
    return {
      label: "Fetch error",
      cls: "border-destructive/30 bg-destructive/10 text-destructive",
      icon: AlertTriangle,
    };
  if (health === "capacity_blocked")
    return {
      label: "Capacity blocked",
      cls: "border-[var(--warning)]/30 bg-[var(--warning)]/10 text-[var(--warning)]",
      icon: AlertTriangle,
    };
  if (health === "zero_stock")
    return {
      label: "Zero stock",
      cls: "border-[var(--warning)]/30 bg-[var(--warning)]/10 text-[var(--warning)]",
      icon: AlertTriangle,
    };
  return {
    label: "Active",
    cls: "border-[var(--success)]/30 bg-[var(--success)]/10 text-[var(--success)]",
    icon: CheckCircle2,
  };
}

type TabId = "overview" | "compute" | "sources" | "advanced";

/* ---------------- page ---------------- */

function ProvidersPage() {
  const [sources, setSources] = useState<Source[]>([]);
  const [offers, setOffers] = useState<MarketOffer[]>([]);
  const [coverageHistory, setCoverageHistory] = useState<Array<Record<string, number | string>>>(
    [],
  );
  const [healthBySource, setHealthBySource] = useState<Record<string, string>>({});
  const [liveSpokes, setLiveSpokes] = useState(0);
  const [applyBusy, setApplyBusy] = useState(false);
  const [applyLogs, setApplyLogs] = useState<string[]>([]);
  const [tab, setTab] = useState<TabId>("overview");
  const [query, setQuery] = useState("");
  const [wizard, setWizard] = useState<{ open: boolean; edit?: Source }>({ open: false });
  const [busy, setBusy] = useState(false);
  const [savingSource, setSavingSource] = useState<Record<string, boolean>>({});

  async function refreshSources() {
    setBusy(true);
    try {
      const [payload, offersPayload, capacityPayload, balancesPayload] = await Promise.all([
        apiRequest<{ sources?: ProviderSourcePublic[] }>("/admin/v1/local/provider-sources", {
          method: "GET",
          timeoutMs: 15000,
        }),
        apiRequest<MarketOffersPayload>("/api/v1/market/offers", {
          method: "GET",
          timeoutMs: 20000,
        }).catch(() => ({ offers: [], source_health: [] })),
        apiRequest<{ entries?: CapacityEntry[] }>("/api/v1/capacity", {
          method: "GET",
          timeoutMs: 12000,
        }).catch(() => ({ entries: [] })),
        apiRequest<ProviderBalancesPayload>("/api/v1/providers/balances", {
          method: "GET",
          timeoutMs: 15000,
        }).catch(() => ({ source_health: [] })),
      ]);

      const rows = Array.isArray(payload.sources) ? payload.sources : [];
      setSources(rows.map(sourceFromAPI));

      const liveOffers = (Array.isArray(offersPayload.offers) ? offersPayload.offers : []).filter(
        (row) => Number(row.stock_available || 0) > 0,
      );
      setOffers(liveOffers);

      const nextHealth: Record<string, string> = {};
      [...(offersPayload.source_health || []), ...(balancesPayload.source_health || [])].forEach(
        (row) => {
          const source = String(row.source || "")
            .trim()
            .toLowerCase();
          if (!source) return;
          const status = String(row.status || row.code || "").trim();
          if (!status) return;
          nextHealth[source] = status;
        },
      );
      setHealthBySource(nextHealth);

      const entries = Array.isArray(capacityPayload.entries) ? capacityPayload.entries : [];
      const spokes = new Set<string>();
      entries.forEach((entry) => {
        const spokeID = String(entry.signal?.spoke_id || "").trim();
        const availCount = Number(entry.signal?.avail_count || 0);
        if (spokeID && availCount > 0) spokes.add(spokeID);
      });
      setLiveSpokes(spokes.size);

      const byProvider: Record<string, number> = {};
      liveOffers.forEach((row) => {
        const key = String(
          row.provider_name || row.provider_slug || row.source || "provider",
        ).trim();
        if (!key) return;
        byProvider[key] = (byProvider[key] || 0) + 1;
      });
      const point: Record<string, number | string> = {
        t: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      };
      Object.entries(byProvider).forEach(([k, v]) => {
        point[k] = v;
      });
      setCoverageHistory((prev) => [...prev.slice(-23), point]);
    } catch (err) {
      if (err instanceof APIError) {
        toast.error(err.detail || "Failed to load provider sources");
      } else {
        toast.error("Failed to load provider sources");
      }
      setSources([]);
      setOffers([]);
      setLiveSpokes(0);
      setHealthBySource({});
    } finally {
      setBusy(false);
    }
  }

  async function refreshApplyLogs() {
    const payload = await apiRequest<{ lines?: string[] }>(
      "/admin/v1/local/logs?source=spoke&tail=200",
      {
        method: "GET",
        timeoutMs: 12000,
      },
    );
    setApplyLogs(Array.isArray(payload.lines) ? payload.lines : []);
  }

  async function applyToSpoke() {
    setApplyBusy(true);
    try {
      const runtime = await apiRequest<{ daemon?: { active_mode?: string } }>(
        "/api/v1/runtime/health",
        {
          method: "GET",
          timeoutMs: 8000,
        },
      ).catch(() => ({}) as { daemon?: { active_mode?: string } });
      const mode = String(runtime.daemon?.active_mode || "")
        .trim()
        .toLowerCase();
      if (mode === "hub_and_spoke") {
        // Desktop mode runs hub/spoke in one daemon process; provider source reads are
        // already request-driven from scoped local config, so shell reload is unnecessary.
        toast.success("Provider settings applied to spoke");
      } else {
        await apiRequest("/admin/v1/local/exec", {
          method: "POST",
          body: { command: "local_load_spoke.sh", timeout_seconds: 220 },
          timeoutMs: 230000,
        });
        toast.success("Spoke reloaded with latest provider sources");
      }
      await Promise.all([refreshSources(), refreshApplyLogs()]);
    } catch (err) {
      if (err instanceof APIError)
        toast.error(err.detail || "Failed to apply provider settings to spoke");
      else toast.error("Failed to apply provider settings to spoke");
      await refreshApplyLogs().catch(() => undefined);
    } finally {
      setApplyBusy(false);
    }
  }

  useEffect(() => {
    void refreshSources();
    void refreshApplyLogs();
  }, []);

  const stats = useMemo(() => {
    const offered = new Set(
      offers
        .map((s) => String(s.provider_name || s.provider_slug || s.source || "").trim())
        .filter(Boolean),
    ).size;
    const active = sources.filter((s) => s.status === "active").length;
    const ready = sources.filter((s) => s.status === "active" && s.hasApiKey && s.baseUrl).length;
    const custom = sources.filter((s) => !s.primary).length;
    const runnable = offers.length;
    const regions = new Set(offers.map((o) => String(o.region_name || "").trim()).filter(Boolean))
      .size;
    const spokes = liveSpokes;
    return { offered, active, ready, custom, runnable, regions, spokes };
  }, [sources, offers, liveSpokes]);

  const providerSeriesKeys = useMemo(() => {
    const counts = new Map<string, number>();
    coverageHistory.forEach((row) => {
      Object.entries(row).forEach(([key, value]) => {
        if (key === "t") return;
        const n = Number(value || 0);
        counts.set(key, (counts.get(key) || 0) + (Number.isFinite(n) ? n : 0));
      });
    });
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([key]) => key);
  }, [coverageHistory]);

  const filtered = sources.filter((s) =>
    [s.key, s.provider, s.baseUrl].some((v) => v.toLowerCase().includes(query.toLowerCase())),
  );

  const computeRows = useMemo(
    () =>
      sources.map((source) => {
        const sourceOffers = offersForSource(offers, source);
        const liveRegions = parseTokenList(
          sourceOffers
            .map((offer) => offer.region_name || "")
            .filter(Boolean)
            .join(","),
        );
        const liveHardware = parseTokenList(
          sourceOffers.map(offerHardwareLabel).filter(Boolean).join(","),
        );
        const rawSample = sourceOffers.map(offerRawMicros).find((value) => value > 0) || undefined;
        return {
          source,
          offers: sourceOffers,
          hardwareOptions: providerHardwareOptions(sourceOffers, source),
          liveRegions,
          liveHardware,
          preview: effectivePrice(source, rawSample),
        };
      }),
    [sources, offers],
  );

  const tabs: { id: TabId; label: string; icon: LucideIcon }[] = [
    { id: "overview", label: "Overview", icon: Activity },
    { id: "compute", label: "Compute", icon: SlidersHorizontal },
    { id: "sources", label: "Sources", icon: Server },
    { id: "advanced", label: "Advanced", icon: Terminal },
  ];

  const updateSource = (key: string, patch: Partial<Source>) => {
    setSources((current) =>
      current.map((source) => (source.key === key ? { ...source, ...patch } : source)),
    );
  };

  const handleSave = async (s: Source, apiKey: string) => {
    try {
      const nextAPIKey = apiKey.trim();
      if (!nextAPIKey && !s.hasApiKey) {
        toast.error("API key is required for new provider sources");
        return;
      }
      const body = providerSourceBody(s, nextAPIKey);
      if (s.sourceId) {
        await apiRequest(`/admin/v1/local/provider-sources/${encodeURIComponent(s.sourceId)}`, {
          method: "PUT",
          body,
          timeoutMs: 20000,
        });
      } else {
        await apiRequest("/admin/v1/local/provider-sources", {
          method: "POST",
          body,
          timeoutMs: 20000,
        });
      }
      toast.success("Provider source saved");
      setWizard({ open: false });
      await refreshSources();
    } catch (err) {
      if (err instanceof APIError) toast.error(err.detail || "Failed to save provider source");
      else toast.error("Failed to save provider source");
    }
  };

  const saveComputePolicy = async (source: Source) => {
    if (!source.sourceId) {
      toast.error("Save the provider source before editing compute listings");
      return;
    }
    setSavingSource((current) => ({ ...current, [source.key]: true }));
    try {
      await apiRequest(`/admin/v1/local/provider-sources/${encodeURIComponent(source.sourceId)}`, {
        method: "PUT",
        body: providerSourceBody(source, ""),
        timeoutMs: 20000,
      });
      toast.success("Compute listing policy saved");
      await refreshSources();
    } catch (err) {
      if (err instanceof APIError) toast.error(err.detail || "Failed to save compute policy");
      else toast.error("Failed to save compute policy");
    } finally {
      setSavingSource((current) => ({ ...current, [source.key]: false }));
    }
  };

  const handleDelete = async (key: string) => {
    const target = sources.find((s) => s.key === key);
    if (!target?.sourceId) {
      setSources((list) => list.filter((s) => s.key !== key));
      return;
    }
    try {
      await apiRequest(`/admin/v1/local/provider-sources/${encodeURIComponent(target.sourceId)}`, {
        method: "DELETE",
      });
      toast.success("Provider source deleted");
      await refreshSources();
    } catch (err) {
      if (err instanceof APIError) toast.error(err.detail || "Failed to delete provider source");
      else toast.error("Failed to delete provider source");
    }
  };

  return (
    <>
      <Topbar
        title="Providers"
        subtitle="Connect provider APIs, manage compute listings, and control seller pricing."
      />
      <main className="flex-1 space-y-6 p-6">
        {/* Hero CTA */}
        <section className="glass relative flex flex-col items-center justify-center gap-3 overflow-hidden rounded-2xl px-6 py-10">
          <div
            aria-hidden
            className="pointer-events-none absolute -left-24 -top-24 h-64 w-64 rounded-full opacity-30 blur-3xl"
            style={{ background: "var(--gradient-primary)" }}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -right-24 -bottom-24 h-64 w-64 rounded-full opacity-20 blur-3xl"
            style={{ background: "var(--gradient-primary)" }}
          />
          <button
            onClick={() => setWizard({ open: true })}
            className="relative inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white shadow-[var(--shadow-glow)]"
            style={{ background: "var(--gradient-primary)" }}
          >
            <Plus className="h-4 w-4" /> New cloud connection
          </button>
          <p className="relative text-[11px] text-muted-foreground">
            Pick a preset, paste a key, broadcast inventory in seconds.
          </p>
        </section>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatChip
            label="Providers"
            value={stats.offered}
            icon={Server}
            hint="Offered in market"
            tone="violet"
          />
          <StatChip
            label="Active spokes"
            value={stats.spokes}
            icon={Activity}
            hint="ACK active"
            tone="mint"
          />
          <StatChip
            label="Runnable cards"
            value={stats.runnable}
            icon={Cpu}
            hint="Stock > 0"
            tone="cyan"
          />
          <StatChip
            label="Regions"
            value={stats.regions}
            icon={Globe2}
            hint="Unique regions"
            tone="amber"
          />
        </div>

        {/* Tabs + actions */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-1 rounded-full border hairline bg-soft p-1 w-fit">
            {tabs.map((t) => {
              const active = tab === t.id;
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-medium transition",
                    active
                      ? "text-white shadow-[var(--shadow-glow)]"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                  style={active ? { background: "var(--gradient-primary)" } : undefined}
                >
                  <Icon className="h-3 w-3" /> {t.label}
                </button>
              );
            })}
          </div>
          <button
            onClick={() => void refreshSources()}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-full border hairline bg-soft px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            <RefreshCcw className={cn("h-3.5 w-3.5", busy && "animate-spin")} /> Refresh
          </button>
        </div>

        {/* OVERVIEW */}
        {tab === "overview" && (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <GlassPanel
              title="Live bid coverage"
              subtitle="Provider card counts over the last 24 snapshots"
              className="lg:col-span-2"
            >
              {coverageHistory.length === 0 || providerSeriesKeys.length === 0 ? (
                <div className="flex h-72 items-center justify-center text-sm text-muted-foreground">
                  Waiting for live offer snapshots.
                </div>
              ) : (
                <div className="h-72 w-full">
                  <ResponsiveContainer>
                    <LineChart
                      data={coverageHistory}
                      margin={{ top: 10, right: 10, left: -16, bottom: 0 }}
                    >
                      <CartesianGrid stroke={gridStroke} strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="t" tick={axisTick} axisLine={false} tickLine={false} />
                      <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                      <Tooltip {...tooltipStyle} />
                      <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} iconType="circle" />
                      {providerSeriesKeys.map((provider, i) => (
                        <Line
                          key={provider}
                          type="monotone"
                          dataKey={provider}
                          stroke={`var(--chart-${(i % 5) + 1})`}
                          strokeWidth={2}
                          dot={false}
                          activeDot={{ r: 4 }}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </GlassPanel>

            <GlassPanel title="Provision sources" subtitle="At a glance">
              <div className="space-y-3">
                <div className="rounded-xl border hairline bg-soft p-4">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Summary
                  </p>
                  <p className="mt-2 text-sm">
                    <span className="font-semibold text-foreground">{stats.active}</span> active ·{" "}
                    <span className="font-semibold text-[var(--success)]">{stats.ready}</span>{" "}
                    broadcast ready ·{" "}
                    <span className="font-semibold text-foreground">{stats.custom}</span> custom
                  </p>
                </div>
                <ul className="space-y-2">
                  {sources.slice(0, 5).map((s) => {
                    const st = sourceStatus(s, healthBySource);
                    const Icon = st.icon;
                    return (
                      <li
                        key={s.key}
                        className="flex items-center justify-between gap-2 rounded-lg border hairline bg-soft px-3 py-2"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-xs font-medium">{s.provider}</p>
                          <p className="truncate font-mono text-[10px] text-muted-foreground">
                            {s.key}
                          </p>
                        </div>
                        <span
                          className={cn(
                            "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium",
                            st.cls,
                          )}
                        >
                          <Icon className="h-2.5 w-2.5" /> {st.label}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </GlassPanel>
          </div>
        )}

        {/* COMPUTE */}
        {tab === "compute" && (
          <div className="space-y-4">
            <GlassPanel
              title="Manage compute listings"
              subtitle={`${stats.runnable} live offers · ${stats.regions} regions · hardware pricing is shown as relative cost bands`}
              action={
                <button
                  onClick={() => setWizard({ open: true })}
                  className="inline-flex items-center gap-1.5 rounded-full border hairline bg-soft px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
                >
                  <Plus className="h-3.5 w-3.5" /> Connect provider
                </button>
              }
            >
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <StatChip
                  label="Sources"
                  value={sources.length}
                  icon={Server}
                  hint="Connected APIs"
                  tone="violet"
                />
                <StatChip
                  label="Hardware"
                  value={new Set(offers.map(offerHardwareLabel)).size}
                  icon={Cpu}
                  hint="Discovered GPUs"
                  tone="cyan"
                />
                <StatChip
                  label="Listed offers"
                  value={offers.length}
                  icon={BadgeDollarSign}
                  hint="Stock available"
                  tone="mint"
                />
              </div>
            </GlassPanel>

            {computeRows.map(
              ({
                source,
                offers: sourceOffers,
                hardwareOptions,
                liveRegions,
                liveHardware,
                preview,
              }) => {
                const st = sourceStatus(source, healthBySource);
                const StatusIcon = st.icon;
                const belowCost =
                  preview.raw > 0 && preview.provider > 0 && preview.provider < preview.raw;
                const saving = !!savingSource[source.key];
                const routeType = source.broadcastMode === "INTERNAL_ONLY" ? "INTERNAL" : "PEER";
                return (
                  <GlassPanel
                    key={source.key}
                    title={source.provider}
                    subtitle={source.key}
                    action={
                      <div className="flex items-center gap-2">
                        <span
                          className={cn(
                            "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium",
                            st.cls,
                          )}
                        >
                          <StatusIcon className="h-3 w-3" /> {st.label}
                        </span>
                        <button
                          onClick={() => void saveComputePolicy(source)}
                          disabled={saving || !source.sourceId}
                          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-white shadow-[var(--shadow-glow)] disabled:opacity-50"
                          style={{ background: "var(--gradient-primary)" }}
                        >
                          <Save className={cn("h-3.5 w-3.5", saving && "animate-pulse")} />{" "}
                          {saving ? "Saving" : "Save"}
                        </button>
                      </div>
                    }
                  >
                    <div className="grid grid-cols-1 gap-3 lg:grid-cols-4">
                      <div className="rounded-xl border hairline bg-soft p-3">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Hardware pricing
                        </p>
                        <p className="mt-2 text-lg font-semibold">Relative bands</p>
                        <p className="text-[10px] text-muted-foreground">exact rates hidden</p>
                      </div>
                      <div className="rounded-xl border hairline bg-soft p-3">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Listing policy
                        </p>
                        <p className="mt-2 text-lg font-semibold">
                          {PRICING_LABEL[source.pricingMode]}
                        </p>
                        <p className="text-[10px] text-muted-foreground">stored server-side</p>
                      </div>
                      <div className="rounded-xl border hairline bg-soft p-3">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Customer view
                        </p>
                        <p className="mt-2 text-lg font-semibold">
                          {source.broadcastMode === "INTERNAL_ONLY"
                            ? "Not broadcast"
                            : "Bands only"}
                        </p>
                        <p className="text-[10px] text-muted-foreground">no numeric rates</p>
                      </div>
                      <div className="rounded-xl border hairline bg-soft p-3">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Live stock
                        </p>
                        <p className="mt-2 text-lg font-semibold">{sourceOffers.length}</p>
                        <p className="text-[10px] text-muted-foreground">
                          {liveRegions.length} regions · {liveHardware.length} hardware types
                        </p>
                      </div>
                    </div>

                    {belowCost && (
                      <div className="mt-3 rounded-xl border border-[var(--warning)]/30 bg-[var(--warning)]/10 p-3 text-xs text-[var(--warning)]">
                        This policy is below the connected provider baseline. The stored policy
                        remains active, while customer hardware continues to show relative bands.
                      </div>
                    )}

                    <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-5">
                      <div className="space-y-3 lg:col-span-3">
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                          <label className="block">
                            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                              Broadcast
                            </span>
                            <select
                              value={source.broadcastMode}
                              onChange={(e) =>
                                updateSource(source.key, {
                                  broadcastMode: e.target.value as BroadcastMode,
                                })
                              }
                              className="h-9 w-full rounded-md border hairline bg-background px-3 text-xs outline-none focus:border-primary/40"
                            >
                              <option value="INTERNAL_ONLY">Internal only</option>
                              <option value="BOTH">Internal + Broadcast</option>
                              <option value="BROADCAST_ONLY">Broadcast only</option>
                            </select>
                          </label>
                          <label className="block">
                            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                              Pricing mode
                            </span>
                            <select
                              value={source.pricingMode}
                              onChange={(e) =>
                                updateSource(source.key, {
                                  pricingMode: e.target.value as PricingMode,
                                })
                              }
                              className="h-9 w-full rounded-md border hairline bg-background px-3 text-xs outline-none focus:border-primary/40"
                            >
                              <option value="PASSTHROUGH">Use provider cost</option>
                              <option value="PREMIUM_OR_DISCOUNT">Markup / discount</option>
                              <option value="FIXED" disabled={source.pricingMode !== "FIXED"}>
                                Fixed seller price
                              </option>
                              <option
                                value="FLOOR_ADJUSTED"
                                disabled={source.pricingMode !== "FLOOR_ADJUSTED"}
                              >
                                Adjusted with floor
                              </option>
                            </select>
                          </label>
                          <label className="block">
                            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                              Adjustment %
                            </span>
                            <input
                              type="number"
                              step="0.1"
                              value={(source.adjustmentBps / 100).toString()}
                              onChange={(e) =>
                                updateSource(source.key, {
                                  adjustmentBps: Math.round((Number(e.target.value) || 0) * 100),
                                })
                              }
                              className="h-9 w-full rounded-md border hairline bg-background px-3 font-mono text-xs outline-none focus:border-primary/40"
                            />
                          </label>
                        </div>

                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                          <div className="rounded-md border hairline bg-background px-3 py-2">
                            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                              Stored price controls
                            </p>
                            <p className="mt-1 text-xs text-muted-foreground">
                              Existing policy values remain applied when saved. Exact hardware rates
                              stay hidden on customer-accessible screens.
                            </p>
                          </div>
                          <label className="block">
                            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                              Stock cap
                            </span>
                            <input
                              type="number"
                              step="1"
                              min="0"
                              value={source.stockLimit ?? ""}
                              onChange={(e) =>
                                updateSource(source.key, {
                                  stockLimit:
                                    Number(e.target.value) > 0
                                      ? Math.floor(Number(e.target.value))
                                      : undefined,
                                })
                              }
                              placeholder="all"
                              className="h-9 w-full rounded-md border hairline bg-background px-3 font-mono text-xs outline-none focus:border-primary/40"
                            />
                          </label>
                        </div>

                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                          <label className="block">
                            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                              Allowed regions
                            </span>
                            <input
                              value={source.allowedRegions.join(", ")}
                              onChange={(e) =>
                                updateSource(source.key, {
                                  allowedRegions: parseTokenList(e.target.value),
                                })
                              }
                              placeholder={liveRegions.slice(0, 3).join(", ") || "all"}
                              className="h-9 w-full rounded-md border hairline bg-background px-3 font-mono text-xs outline-none focus:border-primary/40"
                            />
                          </label>
                          <label className="block">
                            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                              Allowed hardware
                            </span>
                            <input
                              value={source.allowedHardware.join(", ")}
                              onChange={(e) =>
                                updateSource(source.key, {
                                  allowedHardware: parseTokenList(e.target.value),
                                })
                              }
                              placeholder={liveHardware.slice(0, 2).join(", ") || "all"}
                              className="h-9 w-full rounded-md border hairline bg-background px-3 font-mono text-xs outline-none focus:border-primary/40"
                            />
                          </label>
                        </div>
                      </div>

                      <div className="lg:col-span-2">
                        <div className="rounded-xl border hairline bg-soft p-3">
                          <div className="mb-3 flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <div className="flex h-8 w-8 items-center justify-center rounded-lg border hairline bg-background text-primary">
                                <Gauge className="h-4 w-4" />
                              </div>
                              <div>
                                <p className="text-sm font-semibold">Live hardware</p>
                                <p className="text-[10px] text-muted-foreground">
                                  <RouteBadge routeType={routeType} className="mr-1" />{" "}
                                  {BROADCAST_LABEL[source.broadcastMode]}
                                </p>
                              </div>
                            </div>
                            <span className="rounded-full border hairline bg-background px-2 py-0.5 text-[10px] text-muted-foreground">
                              {hardwareOptions.length} options
                            </span>
                          </div>

                          <div className="max-h-60 space-y-2 overflow-auto pr-1">
                            {hardwareOptions.map((hardware) => (
                              <div
                                key={`${source.key}-${hardware.key}`}
                                className="rounded-lg border hairline bg-background/70 p-2"
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="min-w-0">
                                    <p className="truncate text-xs font-semibold">
                                      {hardware.label}
                                    </p>
                                    <p className="truncate text-[10px] text-muted-foreground">
                                      {hardware.regions.slice(0, 2).join(", ") || "region unknown"}{" "}
                                      · stock {hardware.stock}
                                    </p>
                                  </div>
                                  <p className="whitespace-nowrap text-xs font-semibold">
                                    {hardwareCostBandLabel(hardware.costBand)}
                                  </p>
                                </div>
                              </div>
                            ))}
                            {hardwareOptions.length === 0 && (
                              <div className="rounded-lg border hairline bg-background/70 p-6 text-center text-xs text-muted-foreground">
                                No eligible live hardware from this source yet.
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </GlassPanel>
                );
              },
            )}
          </div>
        )}

        {/* SOURCES */}
        {tab === "sources" && (
          <GlassPanel
            title="Provision sources"
            subtitle={`${stats.active} active · ${stats.ready} broadcast ready · ${stats.custom} custom`}
            action={
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search sources…"
                  className="h-8 w-56 rounded-full border hairline bg-soft pl-8 pr-3 text-xs outline-none placeholder:text-muted-foreground focus:border-primary/40"
                />
              </div>
            }
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b hairline text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="px-2 py-2.5 text-left font-medium">Connection key</th>
                    <th className="px-2 py-2.5 text-left font-medium">Provider</th>
                    <th className="px-2 py-2.5 text-left font-medium">Broadcast</th>
                    <th className="px-2 py-2.5 text-left font-medium">Pricing</th>
                    <th className="px-2 py-2.5 text-left font-medium">Hardware pricing</th>
                    <th className="px-2 py-2.5 text-left font-medium">Status</th>
                    <th className="px-2 py-2.5 text-right font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s) => {
                    const st = sourceStatus(s, healthBySource);
                    const Icon = st.icon;
                    const broadcastRoute =
                      s.broadcastMode === "INTERNAL_ONLY" ? "INTERNAL" : "PEER";
                    return (
                      <tr key={s.key} className="border-b hairline transition hover:bg-soft">
                        <td className="px-2 py-3">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-[12px] font-medium">{s.key}</span>
                            {s.primary && (
                              <span className="rounded-full border hairline bg-soft px-1.5 py-0.5 text-[10px] text-muted-foreground">
                                primary
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-3">{s.provider}</td>
                        <td className="px-2 py-3">
                          <div className="flex items-center gap-2">
                            <RouteBadge routeType={broadcastRoute} />
                            <span className="text-[11px] text-muted-foreground">
                              {BROADCAST_LABEL[s.broadcastMode]}
                            </span>
                          </div>
                        </td>
                        <td className="px-2 py-3 text-[11px]">
                          <p>{PRICING_LABEL[s.pricingMode]}</p>
                          {s.pricingMode === "PREMIUM_OR_DISCOUNT" && (
                            <p
                              className={cn(
                                "font-mono text-[10px]",
                                s.adjustmentBps >= 0 ? "text-[var(--success)]" : "text-destructive",
                              )}
                            >
                              {s.adjustmentBps >= 0 ? "+" : ""}
                              {(s.adjustmentBps / 100).toFixed(2)}%
                            </p>
                          )}
                        </td>
                        <td className="px-2 py-3">
                          <span className="text-[11px] text-muted-foreground">
                            Relative bands only
                          </span>
                        </td>
                        <td className="px-2 py-3">
                          <span
                            className={cn(
                              "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium",
                              st.cls,
                            )}
                          >
                            <Icon className="h-3 w-3" /> {st.label}
                          </span>
                        </td>
                        <td className="px-2 py-3 text-right">
                          {s.primary ? (
                            <span className="text-[11px] text-muted-foreground">Managed</span>
                          ) : (
                            <div className="inline-flex items-center gap-1">
                              <button
                                onClick={() => setWizard({ open: true, edit: s })}
                                className="inline-flex h-7 w-7 items-center justify-center rounded-full border hairline bg-soft text-muted-foreground hover:text-foreground"
                              >
                                <Pencil className="h-3 w-3" />
                              </button>
                              <button
                                onClick={() => handleDelete(s.key)}
                                className="inline-flex h-7 w-7 items-center justify-center rounded-full border hairline bg-soft text-muted-foreground hover:text-destructive"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={7} className="py-10 text-center text-xs text-muted-foreground">
                        No sources match “{query}”.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </GlassPanel>
        )}

        {/* ADVANCED */}
        {tab === "advanced" && (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <GlassPanel
              title="Apply to spoke"
              subtitle="Restart the spoke with current Provider Link config"
            >
              <p className="text-xs text-muted-foreground">
                After changing advanced provider link variables, push them to the running spoke.
                This stops and reloads it with your latest source bindings.
              </p>
              <div className="mt-4 flex items-center gap-2">
                <button
                  onClick={() => {
                    void applyToSpoke();
                  }}
                  disabled={applyBusy}
                  className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold text-white shadow-[var(--shadow-glow)]"
                  style={{ background: "var(--gradient-primary)" }}
                >
                  <Zap className={cn("h-3 w-3", applyBusy && "animate-pulse")} />{" "}
                  {applyBusy ? "Applying…" : "Save & apply to spoke"}
                </button>
                <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  <CheckCircle2 className="h-3 w-3 text-[var(--success)]" /> Uses live daemon
                  control APIs
                </span>
              </div>
            </GlassPanel>

            <GlassPanel title="Apply log" subtitle="Runtime log stream is available in System">
              <pre className="max-h-56 overflow-auto rounded-xl border hairline bg-soft p-3 font-mono text-[11px] leading-relaxed text-muted-foreground">
                {applyLogs.length ? applyLogs.join("\n") : "No spoke logs available yet."}
              </pre>
            </GlassPanel>
          </div>
        )}
      </main>

      {wizard.open && (
        <ProviderWizard
          edit={wizard.edit}
          onClose={() => setWizard({ open: false })}
          onSave={handleSave}
        />
      )}
    </>
  );
}

/* ---------------- Wizard ---------------- */

const PRESETS = [
  {
    id: "runpod",
    name: "RunPod",
    base: "https://api.runpod.io",
    offers: "/graphql",
    key: "runpod",
  },
  {
    id: "lambda_cloud",
    name: "Lambda Cloud",
    base: "https://cloud.lambda.ai",
    offers: "/api/v1/instance-types",
    key: "lambda_cloud",
  },
  {
    id: "nscale",
    name: "Nscale",
    base: "https://compute.nks.europe-west4.nscale.com",
    offers: "/api/v1/organizations/{org}/regions",
    key: "nscale",
  },
  {
    id: "hyperstack",
    name: "Hyperstack",
    base: "https://infrahub-api.nexgencloud.com/v1",
    offers: "/core/flavors",
    key: "hyperstack_main",
  },
  { id: "custom", name: "Custom", base: "", offers: "", key: "" },
] as const;

const RESERVED_SOURCE_SLUGS = new Set(["hyperstack", "external", "sim"]);

function slug(s: string) {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

function safeSourceSlug(raw: string) {
  const value = slug(raw || "source");
  if (!value) return "source";
  if (RESERVED_SOURCE_SLUGS.has(value)) return `${value}_main`;
  return value;
}

function ProviderWizard({
  edit,
  onClose,
  onSave,
}: {
  edit?: Source;
  onClose: () => void;
  onSave: (s: Source, apiKey: string) => void | Promise<void>;
}) {
  const [step, setStep] = useState(1);
  const [presetId, setPresetId] = useState<string>(edit ? "custom" : "runpod");
  const [name, setName] = useState(edit?.provider ?? "RunPod");
  const [key, setKey] = useState(edit?.key ?? "runpod");
  const [link, setLink] = useState(true);
  const [baseUrl, setBaseUrl] = useState(edit?.baseUrl ?? "https://api.runpod.io");
  const [apiKey, setApiKey] = useState("");
  const [offersPath, setOffersPath] = useState(edit?.offersPath ?? "/graphql");
  const [balancePath, setBalancePath] = useState(
    edit?.balancePath ?? "/billing/user-credit/credit",
  );
  const [keyHeader, setKeyHeader] = useState(edit?.apiKeyHeader ?? "Authorization");
  const [broadcastMode, setBroadcastMode] = useState<BroadcastMode>(
    edit?.broadcastMode ?? "INTERNAL_ONLY",
  );
  const [unlocked, setUnlocked] = useState({ offers: false, balance: false, header: false });

  const steps = ["Provider", "API"] as const;

  const choosePreset = (p: (typeof PRESETS)[number]) => {
    setPresetId(p.id);
    if (p.id !== "custom") {
      setName(p.name);
      setKey(p.key);
      setBaseUrl(p.base);
      setOffersPath(p.offers);
      if (p.id === "runpod") {
        setBalancePath("/graphql");
        setKeyHeader("Authorization");
      } else if (p.id === "hyperstack") {
        setBalancePath("/billing/user-credit/credit");
        setKeyHeader("api_key");
      } else {
        setBalancePath("/billing/user-credit/credit");
        setKeyHeader("Authorization");
      }
    } else {
      setName("");
      setKey("");
      setBaseUrl("");
      setOffersPath("");
      setBalancePath("/billing/user-credit/credit");
      setKeyHeader("Authorization");
    }
  };

  const onName = (v: string) => {
    setName(v);
    if (!edit) setKey(safeSourceSlug(v));
  };

  const canContinue =
    (step === 1 && name.trim() && key.trim()) ||
    (step === 2 && baseUrl.trim() && (apiKey.trim() || !!edit?.hasApiKey));

  const submit = () => {
    const safeKey = safeSourceSlug(key || name);
    void onSave(
      {
        key: safeKey,
        sourceId: edit?.sourceId,
        provider: name,
        baseUrl,
        offersPath,
        balancePath,
        apiKeyHeader: keyHeader,
        provisionAPIKeyHeader: keyHeader,
        status: link ? "active" : "paused",
        primary: edit?.primary ?? false,
        hasApiKey: true,
        broadcastMode,
        pricingMode: edit?.pricingMode ?? "PASSTHROUGH",
        adjustmentBps: edit?.adjustmentBps ?? 0,
        fixedUnitMicros: edit?.fixedUnitMicros ?? MICRO,
        minUnitMicros: edit?.minUnitMicros ?? 0,
        maxUnitMicros: edit?.maxUnitMicros ?? 0,
        thresholdMicros: edit?.thresholdMicros ?? 0,
        stockLimit: edit?.stockLimit,
        allowedRegions: edit?.allowedRegions ?? [],
        allowedHardware: edit?.allowedHardware ?? [],
      },
      apiKey,
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-background/70 backdrop-blur-md" onClick={onClose} />
      <div className="glass-strong relative z-10 w-full max-w-3xl overflow-hidden rounded-2xl">
        <header className="flex items-start justify-between border-b hairline p-5">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
              {edit ? "Edit cloud connection" : "New cloud connection"}
            </p>
            <h2 className="mt-1 font-display text-lg font-semibold tracking-tight">
              {steps[step - 1]}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="rounded-full border hairline bg-soft p-1.5 text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </header>

        <div className="flex items-center gap-2 border-b hairline px-5 py-3">
          {steps.map((s, i) => {
            const n = i + 1;
            const active = n === step;
            const done = n < step;
            return (
              <div key={s} className="flex flex-1 items-center gap-2">
                <span
                  className={cn(
                    "flex h-6 w-6 items-center justify-center rounded-full border text-[11px] font-semibold transition",
                    done
                      ? "border-transparent text-white"
                      : active
                        ? "border-primary text-primary"
                        : "hairline text-muted-foreground",
                  )}
                  style={done ? { background: "var(--gradient-primary)" } : undefined}
                >
                  {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : n}
                </span>
                <span
                  className={cn(
                    "text-xs font-medium",
                    active ? "text-foreground" : "text-muted-foreground",
                  )}
                >
                  {s}
                </span>
                {i < steps.length - 1 && <div className="ml-1 h-px flex-1 bg-[var(--border)]" />}
              </div>
            );
          })}
        </div>

        <div className="max-h-[60vh] overflow-y-auto p-5">
          {/* STEP 1 */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Choose a preset
                </p>
                <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
                  {PRESETS.map((p) => {
                    const sel = presetId === p.id;
                    return (
                      <button
                        key={p.id}
                        onClick={() => choosePreset(p)}
                        className={cn(
                          "rounded-xl border p-3 text-left transition",
                          sel
                            ? "border-primary bg-soft shadow-[var(--shadow-glow)]"
                            : "hairline bg-soft hover:border-primary/40",
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <div className="flex h-7 w-7 items-center justify-center rounded-lg border hairline bg-background text-[10px] font-semibold uppercase">
                            {p.name.slice(0, 2)}
                          </div>
                          <p className="text-sm font-medium">{p.name}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Field
                  label="Provider name"
                  value={name}
                  onChange={onName}
                  placeholder="My provider"
                />
                <Field
                  label="Connection key"
                  value={key}
                  onChange={setKey}
                  placeholder="my_provider"
                  mono
                />
              </div>

              <label className="flex items-center justify-between rounded-xl border hairline bg-soft p-3">
                <div>
                  <p className="text-sm font-medium">Connect this provider to this daemon</p>
                  <p className="text-[11px] text-muted-foreground">
                    Activate the source so this daemon can use the provider.
                  </p>
                </div>
                <button
                  onClick={() => setLink((v) => !v)}
                  className={cn(
                    "relative h-6 w-11 rounded-full border transition",
                    link ? "border-transparent" : "hairline bg-background",
                  )}
                  style={link ? { background: "var(--gradient-primary)" } : undefined}
                >
                  <span
                    className={cn(
                      "absolute top-1/2 h-4 w-4 -translate-y-1/2 rounded-full bg-white shadow transition-all",
                      link ? "left-6" : "left-1",
                    )}
                  />
                </button>
              </label>

              <div className="rounded-xl border hairline bg-soft p-3">
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Broadcast mode
                </p>
                <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                  {(
                    [
                      {
                        id: "INTERNAL_ONLY",
                        label: "Internal only",
                        hint: "Visible only to this account/daemon",
                      },
                      {
                        id: "BOTH",
                        label: "Internal + Broadcast",
                        hint: "Use locally and share capacity to peers",
                      },
                      {
                        id: "BROADCAST_ONLY",
                        label: "Broadcast only",
                        hint: "Share to peers, not for local jobs",
                      },
                    ] as const
                  ).map((mode) => {
                    const active = broadcastMode === mode.id;
                    return (
                      <button
                        key={mode.id}
                        onClick={() => setBroadcastMode(mode.id)}
                        className={cn(
                          "rounded-lg border p-2 text-left transition",
                          active
                            ? "border-primary bg-background shadow-[var(--shadow-glow)]"
                            : "hairline bg-background/60 hover:border-primary/40",
                        )}
                      >
                        <p className="text-xs font-medium">{mode.label}</p>
                        <p className="mt-0.5 text-[10px] text-muted-foreground">{mode.hint}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <div className="space-y-3">
              <Field
                label="Base URL"
                value={baseUrl}
                onChange={setBaseUrl}
                placeholder="https://api.provider.com"
                mono
                icon={Link2}
              />
              <Field
                label="API key"
                value={apiKey}
                onChange={setApiKey}
                placeholder="sk-..."
                mono
                icon={KeyRound}
                type="password"
              />
              {edit?.hasApiKey && !apiKey.trim() && (
                <p className="text-[11px] text-muted-foreground">
                  API key is already stored. Leave blank to keep it.
                </p>
              )}

              <div className="rounded-xl border hairline bg-soft p-3">
                <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <ShieldCheck className="h-3 w-3" /> Pre-locked defaults · only edit if provider
                  docs differ
                </p>
                <div className="space-y-2">
                  <LockedField
                    label="Offers path"
                    value={offersPath}
                    onChange={setOffersPath}
                    unlocked={unlocked.offers}
                    toggle={() => setUnlocked((u) => ({ ...u, offers: !u.offers }))}
                  />
                  <LockedField
                    label="Balance path"
                    value={balancePath}
                    onChange={setBalancePath}
                    unlocked={unlocked.balance}
                    toggle={() => setUnlocked((u) => ({ ...u, balance: !u.balance }))}
                  />
                  <LockedField
                    label="API key header"
                    value={keyHeader}
                    onChange={setKeyHeader}
                    unlocked={unlocked.header}
                    toggle={() => setUnlocked((u) => ({ ...u, header: !u.header }))}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <footer className="flex items-center justify-between border-t hairline p-4">
          <div className="text-[11px] text-muted-foreground">
            {step === 1 &&
              (name ? (
                <span className="inline-flex items-center gap-1.5">
                  <CheckCircle2 className="h-3 w-3 text-[var(--success)]" /> {name} · key{" "}
                  <span className="font-mono">{key || "—"}</span>
                </span>
              ) : (
                "Pick a preset or name your provider"
              ))}
            {step === 2 &&
              (apiKey ? (
                <span className="inline-flex items-center gap-1.5">
                  <ShieldCheck className="h-3 w-3 text-primary" /> Credentials look good
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5">
                  <AlertTriangle className="h-3 w-3 text-[var(--warning)]" /> API key required
                </span>
              ))}
          </div>
          <div className="flex items-center gap-2">
            {step > 1 && (
              <button
                onClick={() => setStep((s) => s - 1)}
                className="inline-flex items-center gap-1 rounded-full border hairline bg-soft px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="h-3 w-3" /> Back
              </button>
            )}
            {step < steps.length ? (
              <button
                disabled={!canContinue}
                onClick={() => setStep((s) => s + 1)}
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold text-white shadow-[var(--shadow-glow)] disabled:opacity-40"
                style={{ background: "var(--gradient-primary)" }}
              >
                Continue <ArrowRight className="h-3 w-3" />
              </button>
            ) : (
              <button
                onClick={submit}
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold text-white shadow-[var(--shadow-glow)]"
                style={{ background: "var(--gradient-primary)" }}
              >
                <CheckCircle2 className="h-3 w-3" /> {edit ? "Save changes" : "Save source"}
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}

/* ---------------- primitives ---------------- */

function Field({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  mono,
  icon: Icon,
}: {
  label: string;
  value?: string;
  onChange?: (v: string) => void;
  placeholder?: string;
  type?: string;
  mono?: boolean;
  icon?: LucideIcon;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <div className="relative">
        {Icon && (
          <Icon className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        )}
        <input
          type={type}
          value={value ?? ""}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder}
          className={cn(
            "h-9 w-full rounded-md border hairline bg-background px-3 text-sm outline-none focus:border-primary/40",
            mono && "font-mono text-[12px]",
            Icon && "pl-8",
          )}
        />
      </div>
    </label>
  );
}

function LockedField({
  label,
  value,
  onChange,
  unlocked,
  toggle,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  unlocked: boolean;
  toggle: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-32 shrink-0 text-[11px] text-muted-foreground">{label}</div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={!unlocked}
        className={cn(
          "h-8 flex-1 rounded-md border hairline bg-background px-2 font-mono text-[12px] outline-none focus:border-primary/40",
          !unlocked && "opacity-60",
        )}
      />
      <button
        onClick={toggle}
        className="inline-flex items-center gap-1 rounded-md border hairline bg-soft px-2 py-1 text-[11px] text-muted-foreground hover:text-foreground"
      >
        {unlocked ? <Unlock className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
        {unlocked ? "Lock" : "Edit"}
      </button>
    </div>
  );
}
